import { saveAs } from 'file-saver'
import JSZip from 'jszip'

// AI Studio project persistence. Captures the FULL studio state into a single
// portable file (download) and restores it (Open Project). Scope is limited to
// AI-owned storage so normal Hexapod/coding/flowchart projects are never
// touched: only these session keys, the appModeStorage localStorage slice, the
// AI-owned ProgrammingPage stores, and the TF.js weights DB are read/written.
const MAGIC = 'PLODE_AI_PROJECT'
const FILE_VERSION = 1
const FILE_EXT = 'plodeai'
// const DEFAULT_ICON =
//     "data:image/svg+xml;utf8,<svg width='59' height='59' viewBox='0 0 59 59' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M58.775 14.419C58.7754 16.3893 58.3867 18.3403 57.6312 20.16C56.8756 21.9798 55.7682 23.6324 54.3724 25.023C52.9765 26.4136 51.3198 27.5149 49.4973 28.2636C47.6747 29.0123 45.7223 29.3938 43.752 29.386H37.969C36.8422 29.3859 35.7265 29.6077 34.6854 30.0388C33.6444 30.47 32.6985 31.102 31.9017 31.8987C31.105 32.6955 30.473 33.6414 30.0419 34.6824C29.6107 35.7235 29.3888 36.8392 29.389 37.966V43.75C29.397 45.7216 29.0153 47.6753 28.2657 49.4989C27.5162 51.3225 26.4135 52.9799 25.0212 54.3759C23.629 55.7719 21.9745 56.879 20.1529 57.6334C18.3313 58.3879 16.3786 58.7748 14.407 58.772C10.5601 58.6971 6.8961 57.1163 4.20221 54.3691C1.50832 51.6219 -0.000466999 47.9276 1.08427e-07 44.08V14.691C0.00238535 10.7947 1.5515 7.05883 4.30695 4.30414C7.06239 1.54944 10.7988 0.00132453 14.695 0H44.082C47.9312 0.00112965 51.6265 1.51208 54.3738 4.20819C57.1212 6.9043 58.7014 10.5704 58.775 14.419Z' fill='%23FFBC45'/><path d='M48.06 57.128C53.0686 57.128 57.129 53.0677 57.129 48.059C57.129 43.0503 53.0686 38.99 48.06 38.99C43.0513 38.99 38.991 43.0503 38.991 48.059C38.991 53.0677 43.0513 57.128 48.06 57.128Z' fill='%23FFBC45'/><path d='M47.78 45.5H49.18V51H48.07L47.82 50.19C47.49 50.75 46.92 51.16 46 51.16C44.42 51.16 43.42 49.98 43.42 48.26C43.42 46.53 44.43 45.34 45.97 45.34C46.82 45.34 47.41 45.72 47.78 46.31V45.5ZM46.33 49.86C47.21 49.86 47.76 49.21 47.76 48.25C47.76 47.3 47.22 46.64 46.33 46.64C45.44 46.64 44.89 47.3 44.89 48.25C44.89 49.2 45.44 49.86 46.33 49.86ZM51.5195 42.39C51.9995 42.39 52.3395 42.74 52.3395 43.26C52.3395 43.73 52.0295 44.15 51.5195 44.15C51.0295 44.15 50.6995 43.77 50.6995 43.26C50.6995 42.73 51.0195 42.39 51.5195 42.39ZM52.2595 51H50.8095V45.5H52.2595V51Z' fill='white'/></svg>"

// sessionStorage keys that make up an AI project (Select Feature, Configure,
// inputs, output layout, and the flowchart code).
const AI_SESSION_KEYS = [
    'aiCodeSelectedFeatures',
    'enable',
    'webAiMode',
    'aiCodeLayout',
    'aiCodeOutputConfig',
    'aiCodeOutputs',
    'aiCodePaths',
    'aiCameraDeviceId',
    'aiCameraFps',
    'aiShowTrail',
    'aiShowTrajectory',
    'aiShowRegion',
    'aiShowBoxes',
    'aiShowLine',
    'aiShowCount',
    'aiCameraAspect',
    'aiMicDeviceId',
    'startButtonState',
    'aiConfigureTab',
    'allDetails',
    'flowchart-elements',
    'flowchart-elements-id',
]

// ProgrammingPage stores owned by AI training. AudioFiles / ProgramData are
// shared with normal-mode action recording, so they are intentionally excluded.
const PROGRAMMINGPAGE_AI_STORES = ['models', 'Samples', 'SavedFaces']
const PROGRAMMINGPAGE_ENSURE = [
    { name: 'models', keyPath: 'id' },
    { name: 'Samples', keyPath: 'id', autoIncrement: true },
    { name: 'SavedFaces', keyPath: 'id' },
]
// TF.js stores its layer models here (all AI-owned; normal coding trains none).
const TFJS_DB = 'tensorflowjs'
const TFJS_ENSURE = [
    { name: 'models_store', keyPath: 'modelPath' },
    { name: 'model_info_store', keyPath: 'modelPath' },
]
// Action-panel audio/video clips (kept out of sessionStorage; allDetails holds
// only a lightweight key that points here). Out-of-line keys — the key is passed
// to put(), so the store has no keyPath.
const MEDIA_DB = 'plode_ai_media'
const MEDIA_ENSURE = [{ name: 'media' }]

const abToB64 = (ab) => {
    const bytes = new Uint8Array(ab)
    let bin = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
    }
    return btoa(bin)
}
const b64ToAb = (b64) => {
    const bin = atob(b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
    return bytes.buffer
}
const blobToB64 = (blob) =>
    new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
        reader.onerror = () => resolve('')
        reader.readAsDataURL(blob)
    })

// Deep-encode a value tree so binary records (ArrayBuffer / TypedArray / Blob),
// which JSON can't hold, survive a round-trip through the project file.
const encodeVal = async (v) => {
    if (v == null || typeof v !== 'object') return v
    if (v instanceof ArrayBuffer) return { __ab: abToB64(v) }
    if (ArrayBuffer.isView(v)) {
        const buf = v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength)
        return { __ta: v.constructor.name, d: abToB64(buf) }
    }
    if (typeof Blob !== 'undefined' && v instanceof Blob) {
        return { __blob: await blobToB64(v), type: v.type }
    }
    if (Array.isArray(v)) {
        const out = []
        for (const item of v) out.push(await encodeVal(item))
        return out
    }
    const out = {}
    for (const key of Object.keys(v)) out[key] = await encodeVal(v[key])
    return out
}
const decodeVal = (v) => {
    if (v == null || typeof v !== 'object') return v
    if (v.__ab !== undefined) return b64ToAb(v.__ab)
    if (v.__ta !== undefined) {
        const Ctor = (typeof window !== 'undefined' && window[v.__ta]) || Array
        return new Ctor(b64ToAb(v.d))
    }
    if (v.__blob !== undefined)
        return new Blob([b64ToAb(v.__blob)], { type: v.type || '' })
    if (Array.isArray(v)) return v.map(decodeVal)
    const out = {}
    for (const key of Object.keys(v)) out[key] = decodeVal(v[key])
    return out
}

const b64ToBytes = (b64) => new Uint8Array(b64ToAb(b64))

// File extension for a mime type, so lifted media are recognisable files in the
// zip (a1.jpg, a2.png, a3.wav, ...) rather than opaque blobs.
const extForMime = (mime) => {
    const m = (mime || '').toLowerCase()
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg'
    if (m.includes('png')) return 'png'
    if (m.includes('webp')) return 'webp'
    if (m.includes('gif')) return 'gif'
    if (m.includes('svg')) return 'svg'
    if (m.includes('webm')) return 'webm'
    if (m.includes('mp4')) return 'mp4'
    if (m.includes('quicktime') || m.includes('mov')) return 'mov'
    if (m.includes('wav')) return 'wav'
    if (m.includes('mpeg') || m.includes('mp3')) return 'mp3'
    if (m.includes('ogg')) return 'ogg'
    if (m.includes('aac')) return 'aac'
    return 'bin'
}

// A data URL like "data:image/jpeg;base64,...." -> { mime, b64 }, else null.
const parseDataUrl = (s) => {
    if (typeof s !== 'string' || !s.startsWith('data:')) return null
    const comma = s.indexOf(',')
    if (comma < 0) return null
    const header = s.slice(5, comma) // e.g. "image/jpeg;base64"
    if (!/;base64$/i.test(header)) return null
    return { mime: header.replace(/;base64$/i, '') || '', b64: s.slice(comma + 1) }
}

// Walk an already-encoded tree and lift each heavy payload out to the `assets`
// map, replacing it with a lightweight { $asset } reference — so the zip holds
// real media files (images/audio/video by extension) and raw weight blobs,
// instead of megabytes of base64 text. decodeVal stays untouched after
// re-hydration by dropAssets.
const liftAssets = (node, assets, ctr) => {
    // Captured training images / uploaded media are stored as base64 data URLs
    // (plain strings). Lift them out as proper media files.
    const du = parseDataUrl(node)
    if (du) {
        const id = `assets/a${ctr.n++}.${extForMime(du.mime)}`
        assets[id] = du.b64
        return { $asset: id, akind: 'dataurl', mime: du.mime }
    }
    if (node == null || typeof node !== 'object') return node
    if (typeof node.__ab === 'string') {
        const id = `assets/a${ctr.n++}.bin`
        assets[id] = node.__ab
        return { $asset: id, akind: 'ab' }
    }
    if (typeof node.__ta === 'string' && typeof node.d === 'string') {
        const id = `assets/a${ctr.n++}.bin`
        assets[id] = node.d
        return { $asset: id, akind: 'ta', ta: node.__ta }
    }
    if (typeof node.__blob === 'string') {
        const id = `assets/a${ctr.n++}.${extForMime(node.type)}`
        assets[id] = node.__blob
        return { $asset: id, akind: 'blob', type: node.type || '' }
    }
    if (Array.isArray(node)) return node.map((n) => liftAssets(n, assets, ctr))
    const out = {}
    for (const k of Object.keys(node)) out[k] = liftAssets(node[k], assets, ctr)
    return out
}

// Reverse of liftAssets: rebuild the original value (data URL string, or the
// __ab/__ta/__blob marker) from the loaded asset base64.
const dropAssets = (node, getB64) => {
    if (node == null || typeof node !== 'object') return node
    if (node.$asset) {
        const b64 = getB64(node.$asset) || ''
        if (node.akind === 'dataurl')
            return `data:${node.mime || ''};base64,${b64}`
        if (node.akind === 'ta') return { __ta: node.ta, d: b64 }
        if (node.akind === 'blob') return { __blob: b64, type: node.type || '' }
        return { __ab: b64 }
    }
    if (Array.isArray(node)) return node.map((n) => dropAssets(n, getB64))
    const out = {}
    for (const k of Object.keys(node)) out[k] = dropAssets(node[k], getB64)
    return out
}

const openIDB = (name, upgradeStores) =>
    new Promise((resolve, reject) => {
        let req
        try {
            req = indexedDB.open(name)
        } catch (e) {
            reject(e)
            return
        }
        req.onupgradeneeded = (e) => {
            const db = req.result
            if (upgradeStores && upgradeStores.length) {
                upgradeStores.forEach((s) => {
                    if (!db.objectStoreNames.contains(s.name)) {
                        db.createObjectStore(s.name, {
                            keyPath: s.keyPath,
                            autoIncrement: !!s.autoIncrement,
                        })
                    }
                })
            } else {
                // We only wanted to read/clear an EXISTING db. This upgrade
                // means it didn't exist and would be created empty (no stores) —
                // abort so we never leave a store-less database behind, which
                // is exactly what makes TF.js model.save() crash later.
                try {
                    e.target.transaction.abort()
                } catch (_) {
                    /* ignore */
                }
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
        req.onblocked = () => reject(new Error(`${name} blocked`))
    })

const deleteDB = (name) =>
    new Promise((resolve) => {
        try {
            const req = indexedDB.deleteDatabase(name)
            req.onsuccess = req.onerror = req.onblocked = () => resolve()
        } catch (e) {
            resolve()
        }
    })

// If the TF.js weights DB exists but is missing its object stores (a corrupted
// state that makes model.save() / listModels() throw an uncatchable error),
// delete it so TF.js recreates it cleanly on the next save. Safe no-op when the
// DB is healthy or absent.
export const repairTfjsDb = async () => {
    try {
        if (typeof indexedDB === 'undefined') return
        if (typeof indexedDB.databases === 'function') {
            const list = await indexedDB.databases()
            if (!(list || []).some((d) => d && d.name === TFJS_DB)) return
        }
        let db
        try {
            db = await openIDB(TFJS_DB) // won't create it (aborts on upgrade)
        } catch (e) {
            return // absent or unopenable — nothing to repair
        }
        if (!db) return
        const healthy = TFJS_ENSURE.every((s) =>
            db.objectStoreNames.contains(s.name)
        )
        db.close()
        if (!healthy) await deleteDB(TFJS_DB)
    } catch (e) {
        /* ignore */
    }
}

const readStore = (db, storeName) =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly')
        const os = tx.objectStore(storeName)
        const keyPath = os.keyPath
        const rq = os.getAll()
        rq.onsuccess = () => {
            const records = rq.result || []
            if (keyPath == null) {
                const kq = os.getAllKeys()
                kq.onsuccess = () =>
                    resolve({ keyPath, records, keys: kq.result || [] })
                kq.onerror = () => reject(kq.error)
            } else {
                resolve({ keyPath, records, keys: null })
            }
        }
        rq.onerror = () => reject(rq.error)
    })

const dumpDB = async (name, onlyStores) => {
    let db
    try {
        db = await openIDB(name)
    } catch {
        return null
    }
    if (!db) return null
    let stores = Array.from(db.objectStoreNames)
    if (onlyStores) stores = stores.filter((s) => onlyStores.includes(s))
    const out = { stores: {} }
    for (const s of stores) {
        const { keyPath, records, keys } = await readStore(db, s)
        out.stores[s] = { keyPath, keys, records: await encodeVal(records) }
    }
    db.close()
    return out
}

const restoreDB = async (name, dump, ensure) => {
    if (!dump || !dump.stores) return
    let db
    try {
        db = await openIDB(name, ensure)
    } catch {
        return
    }
    if (!db) return
    const storeNames = Object.keys(dump.stores).filter((s) =>
        db.objectStoreNames.contains(s)
    )
    if (!storeNames.length) {
        db.close()
        return
    }
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeNames, 'readwrite')
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
        storeNames.forEach((s) => {
            const os = tx.objectStore(s)
            os.clear()
            const { records, keyPath, keys } = dump.stores[s]
            const decoded = decodeVal(records) || []
            decoded.forEach((rec, i) => {
                if (keyPath == null && keys) os.put(rec, decodeVal(keys[i]))
                else os.put(rec)
            })
        })
    })
    db.close()
}

const clearDBStores = async (name, onlyStores) => {
    let db
    try {
        db = await openIDB(name)
    } catch {
        return
    }
    if (!db) return
    const storeNames = Array.from(db.objectStoreNames).filter(
        (s) => !onlyStores || onlyStores.includes(s)
    )
    if (!storeNames.length) {
        db.close()
        return
    }
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeNames, 'readwrite')
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
        storeNames.forEach((s) => tx.objectStore(s).clear())
    })
    db.close()
}

// Gather the whole AI project into a JSON-safe object.
export const exportAiProject = async (name, metadata) => {
    const session = {}
    AI_SESSION_KEYS.forEach((k) => {
        const v = sessionStorage.getItem(k)
        if (v !== null) session[k] = v
    })
    const local = {}
    const appMode = localStorage.getItem('appModeStorage')
    if (appMode !== null) local.appModeStorage = appMode

    const [programmingPage, tfModels, media] = await Promise.all([
        dumpDB('ProgrammingPage', PROGRAMMINGPAGE_AI_STORES),
        dumpDB(TFJS_DB),
        dumpDB(MEDIA_DB),
    ])

    return {
        magic: MAGIC,
        version: FILE_VERSION,
        kind: 'ai-studio',
        name: name || '',
        // metadata: {
        //     createdAt: new Date().toISOString(),
        //     icon: DEFAULT_ICON,
        // },
        session,
        local,
        idb: {
            ProgrammingPage: programmingPage,
            tensorflowjs: tfModels,
            plode_ai_media: media,
        },
    }
}

// Wipe only AI-owned state (New Project / pre-import reset).
export const clearAiProject = async () => {
    AI_SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k))
    // appModeStorage lives in BOTH stores (the reducer mirrors it to each, and
    // loadInitialState reads sessionStorage first) — clear both so a stale copy
    // can't shadow a freshly imported project.
    localStorage.removeItem('appModeStorage')
    sessionStorage.removeItem('appModeStorage')
    await clearDBStores('ProgrammingPage', PROGRAMMINGPAGE_AI_STORES)
    await clearDBStores(TFJS_DB)
    await clearDBStores(MEDIA_DB)
}

export const importAiProject = async (data) => {
    if (!data || data.magic !== MAGIC) {
        throw new Error('This file is not a Plode AI Studio project.')
    }
    await clearAiProject()
    Object.entries(data.session || {}).forEach(([k, v]) =>
        sessionStorage.setItem(k, v)
    )
    if (data.local && data.local.appModeStorage != null) {
        // Write BOTH: the reducer keeps them mirrored and loadInitialState reads
        // sessionStorage first, so both must hold the imported slice.
        localStorage.setItem('appModeStorage', data.local.appModeStorage)
        sessionStorage.setItem('appModeStorage', data.local.appModeStorage)
    }
    const idb = data.idb || {}
    if (idb.ProgrammingPage)
        await restoreDB(
            'ProgrammingPage',
            idb.ProgrammingPage,
            PROGRAMMINGPAGE_ENSURE
        )
    if (idb.tensorflowjs)
        await restoreDB(TFJS_DB, idb.tensorflowjs, TFJS_ENSURE)
    if (idb.plode_ai_media)
        await restoreDB(MEDIA_DB, idb.plode_ai_media, MEDIA_ENSURE)
}

// Save the project as a ZIP: project.json (session/local + IDB structure with
// binary refs) plus an assets/ folder holding the trained-model weights and
// captured images/audio as real binary files.
export const downloadAiProject = async (name) => {
    const data = await exportAiProject(name)
    const assets = {}
    const light = { ...data, idb: liftAssets(data.idb, assets, { n: 0 }) }

    const zip = new JSZip()
    zip.file('project.json', JSON.stringify(light))
    Object.entries(assets).forEach(([id, b64]) => {
        zip.file(id, b64ToBytes(b64))
    })
    const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
    })
    saveAs(blob, `${name || 'ai-project'}.${FILE_EXT}`)
    return data
}

export const openAiProjectFile = async (file) => {
    const buf = await file.arrayBuffer()
    const head = new Uint8Array(buf.slice(0, 4))
    const isZip = head[0] === 0x50 && head[1] === 0x4b // "PK"
    if (isZip) {
        const zip = await JSZip.loadAsync(buf)
        const pj = zip.file('project.json')
        if (!pj) throw new Error('This file is not a Plode AI Studio project.')
        const light = JSON.parse(await pj.async('string'))
        // Preload every asset as base64 so re-hydration can run synchronously.
        const assetB64 = {}
        const assetNames = Object.keys(zip.files).filter(
            (n) => n.startsWith('assets/') && !zip.files[n].dir
        )
        await Promise.all(
            assetNames.map(async (n) => {
                assetB64[n] = await zip.file(n).async('base64')
            })
        )
        const data = {
            ...light,
            idb: dropAssets(light.idb, (id) => assetB64[id]),
        }
        await importAiProject(data)
        return data
    }
    // Legacy single-JSON .plodeai (pre-zip) — still importable.
    const text = new TextDecoder().decode(buf)
    const data = JSON.parse(text)
    await importAiProject(data)
    return data
}

export const AI_PROJECT_EXT = FILE_EXT
